import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ShieldCheck, KeyRound, Mail } from "lucide-react";
import { apiRequest, queryClient, setAuthToken } from "@/lib/queryClient";
import { primeAuth, AUTH_ME_KEY } from "@/lib/auth";

type BeginResp = {
  challengeId: string;
  expiresAt: string;
  maskedEmail: string;
  deliveredViaEmail: boolean;
  devCode?: string | null;
};

export default function MasterLoginPage() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<"creds" | "otp">("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<BeginResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!challenge) return;
    const tick = () => {
      const ms = new Date(challenge.expiresAt).getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [challenge]);

  const beginMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/master/begin", { email, password });
      return (await res.json()) as BeginResp;
    },
    onSuccess: (data) => {
      setChallenge(data);
      setStep("otp");
      setCode("");
      setError(null);
    },
    onError: (err: any) => setError(err?.message || "Sign-in failed"),
  });

  const verifyMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/master/verify", { challengeId: challenge!.challengeId, code });
      return await res.json();
    },
    onSuccess: async (body: any) => {
      if (body?.token) setAuthToken(body.token);
      // Re-prime auth so the freshly-issued token's role is in the cache
      // before we decide where to navigate. Master Admin always holds the
      // super_admin role, which must land on /admin — not "/" (which would
      // render the analytics Dashboard or StaffPortal depending on features).
      const me = await primeAuth();
      const role = me?.user?.role;
      const home = role === "super_admin" ? "/admin" : "/";
      // body.redirectTo is respected for deep-link bookmarks (?redirect=…)
      // but must not override the role-based home when it points to root.
      const target = body?.redirectTo && body.redirectTo !== "/" ? body.redirectTo : home;
      navigate(target);
    },
    onError: (err: any) => setError(err?.message || "Verification failed"),
  });

  function fmt(s: number) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <Card className="w-full max-w-md border-2 border-amber-500/30 shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center" aria-hidden="true">
            <ShieldCheck className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <CardTitle>Master Admin</CardTitle>
          <CardDescription>
            {step === "creds"
              ? "Restricted access. Two-step verification required."
              : `We sent a 6-digit code to ${challenge?.maskedEmail}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="alert-master-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === "creds" && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                beginMut.mutate();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="master-email">Email</Label>
                <Input
                  id="master-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="input-master-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="master-password">Password</Label>
                <Input
                  id="master-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="input-master-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={beginMut.isPending}
                data-testid="button-master-continue"
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {beginMut.isPending ? "Sending code…" : "Continue"}
              </Button>
            </form>
          )}

          {step === "otp" && challenge && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                verifyMut.mutate();
              }}
            >
              <div className="flex flex-col items-center space-y-3">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  data-testid="input-master-otp"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <div className="text-xs text-muted-foreground">
                  {secondsLeft > 0 ? `Expires in ${fmt(secondsLeft)}` : "Code expired — request a new one."}
                </div>
              </div>

              {challenge.devCode && (
                <Alert>
                  <Mail className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Dev mode:</strong> email isn't configured, so the code is
                    <code className="ml-2 px-2 py-0.5 rounded bg-muted font-mono">{challenge.devCode}</code>.
                    Set up SendGrid or SMTP from <strong>Master Admin → Integrations</strong> after sign-in.
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={code.length !== 6 || verifyMut.isPending}
                data-testid="button-master-verify"
              >
                {verifyMut.isPending ? "Verifying…" : "Verify & sign in"}
              </Button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  onClick={() => {
                    setStep("creds");
                    setChallenge(null);
                    setCode("");
                    setError(null);
                  }}
                  data-testid="button-master-back"
                >
                  ← Use a different account
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  onClick={() => {
                    setError(null);
                    beginMut.mutate();
                  }}
                  disabled={beginMut.isPending}
                  data-testid="button-master-resend"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
